'use client';

/**
 * Dependency imports
 */
import React from 'react';
import Link from 'next/link';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Container, Group,} from '@mantine/core';

/**
 * Local imports
 */
import UnderlineLink from './UnderlineLink';
import LogoImage from '../../public/img/logo.svg';
import LoginButton from './auth/LoginButton';

/**
 * SiteHeader Component
 */
const Root = styled('header', {
  shouldForwardProp: (prop: PropertyKey) => !['scheme', 'mobileMenuOpen'].includes(prop.toString()),
})<Partial<SiteHeaderCombinedProps> & { mobileMenuOpen: boolean }>(
  ({ scheme = 'dark', mobileMenuOpen = false }) => css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100px;
    z-index: 199;
    display: flex;
    align-items: center;
    color: ${{
      light: '#fff',
      dark: '#222',
    }[scheme]};
    transition: 150ms height ease-in-out, 150ms color ease-in-out,
      150ms background-color ease-in-out, 150ms box-shadow ease-in-out;

    > div {
      display: flex;
      align-items: center;
      flex: 1;
    }

    #site-logo {
      width: 280px;
      transform-origin: left center;
      transition: 150ms transform ease-in-out, 200ms color ease-in-out;

      svg {
        display: block;
        color: inherit;
      }

      @media screen and (max-width: 992px) {
        color: ${mobileMenuOpen ? '#333' : 'inherit'};
      }
    }

    #site-nav {
      display: flex;
      align-items: center;
      margin-left: 3rem;

      @media screen and (max-width: 992px) {
        position: absolute;
        top: 0;
        left: 0;
        z-index: -1;
        width: 100%;
        height: 100vh;
        margin: 0;
        padding: 2rem;
        padding-bottom: 10rem;
        flex-direction: column;
        justify-content: center;
        font-size: 2rem;
        font-weight: 700;
        color: #222;
        background: var(--color-primary);
        transform: translateX(${mobileMenuOpen ? 0 : '100%'});
        transition: 220ms transform ease-in-out;
      }
    }

    #site-secondary-nav {
      @media screen and (max-width: 992px) {
        position: fixed;
        bottom: 20px;
        left: 0;
        width: 100%;
        justify-content: center;
        color: #222;
        transform: translateX(${mobileMenuOpen ? 0 : '100%'});
        transition: 220ms transform ease-in-out;
      }
    }

    #mobile-menu-toggle {
      display: none;

      @media screen and (max-width: 992px) {
        position: relative;
        display: block;
        margin-left: auto;
        width: 3rem;
        height: 3rem;
        font-size: 0;
        color: transparent;
        border: none;
        background: none;
        transition: 250ms transform ease-in-out;
        transform: ${mobileMenuOpen ? 'rotate(-180deg)' : 'rotate(0)'};

        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 2rem;
          height: 0.25rem;
          background-color: ${mobileMenuOpen
            ? '#333'
            : {
                light: '#fff',
                dark: '#222',
              }[scheme]};
          transition: 250ms transform ease-in-out;
        }

        &::before {
          transform: ${mobileMenuOpen
            ? 'translate(-50%, -50%) rotate(45deg)'
            : 'translate(-50%, calc(-50% - 0.5rem))'};
        }

        &::after {
          transform: ${mobileMenuOpen
            ? 'translate(-50%, -50%) rotate(-45deg)'
            : 'translate(-50%, calc(-50% + 0.5rem))'};
        }

        > span {
          display: block;
          width: 2rem;
          height: 0.25rem;
          margin: 0 auto;
          background-color: ${mobileMenuOpen
            ? '#333'
            : {
                light: '#fff',
                dark: '#222',
              }[scheme]};
          opacity: ${mobileMenuOpen ? 0 : 1};
          transition: 250ms opacity ease-in-out;
        }
      }
    }

    body.scrolled & {
      height: 70px;
      color: #222;
      background-color: #ffffff;
      box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.15);
      transition-delay: 0ms, 0ms, 150ms, 0ms;

      #site-logo {
        transform: scale(0.8);
      }

      #mobile-menu-toggle {
        &::before,
        &::after {
          background-color: #333;
        }

        > span {
          background-color: #333;
        }
      }
    }
  `
);

export interface SiteHeaderProps {
  scheme?: 'light' | 'dark';
}

export type SiteHeaderCombinedProps = SiteHeaderProps & JSX.IntrinsicElements['header'];

const SiteHeader: React.FC<SiteHeaderCombinedProps> = ({ scheme, ...props }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        document.body.classList.add('scrolled');
      } else {
        document.body.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Root scheme={scheme} {...props} mobileMenuOpen={mobileMenuOpen}>
      <Container size="xl">
        <Link href="/" id="site-logo">
          <LogoImage />
        </Link>

        <button id="mobile-menu-toggle" onClick={toggleMobileMenu}>
          <span>Toggle Menu</span>
        </button>

        <nav id="site-nav">
          <UnderlineLink href="/">Home</UnderlineLink>
          <UnderlineLink href="/blog">Blog</UnderlineLink>
          <UnderlineLink href="/pricing">Pricing</UnderlineLink>
          <UnderlineLink href="/streamline-condo-transactions-with-homehealth-solutions">Document Review</UnderlineLink>
          <UnderlineLink href="/contact">Contact</UnderlineLink>
        </nav>

        <Group id="site-secondary-nav" ml="auto">
          <LoginButton />
        </Group>
      </Container>
    </Root>
  );
};

export default SiteHeader;
